import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 50,
        paddingHorizontal: 60,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
    },
    companyInfoLeft: {
        flexGrow: 1,
        textAlign: 'left',
    },
    companyName: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 16,
        color: '#333',
        marginBottom: 3,
    },
    companyAddress: {
        fontSize: 9,
        color: '#666',
        marginBottom: 2,
    },
    logoContainer: {
        width: 100,
        height: 100,
        marginLeft: 20,
        marginTop: -10
    },
    logo: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: 15,
    },
    title: {
        fontSize: 16,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#222',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginTop: 15,
    },
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
        marginTop: 5,
    },
    infoColumn: { width: '33%' },
    infoLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 10, color: '#444', marginBottom: 2 },
    infoText: { fontSize: 10, color: '#666', marginBottom: 5 },

    contentBox: {
        borderWidth: 1,
        borderColor: '#EEE',
        padding: 15,
        marginTop: 10,
        backgroundColor: '#FAFAFA',
    },
    sectionTitle: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 11,
        marginBottom: 8,
        color: '#333',
        textTransform: 'uppercase',
    },
    totalsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 20,
    },
    totalsContainer: {
        width: '45%',
        borderTopWidth: 1.5,
        borderTopColor: '#E0E0E0',
        paddingTop: 10,
    },
    grandTotalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    grandTotalLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13 },
    grandTotalValue: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13 },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888',
        paddingHorizontal: 60,
    },
});

const ReporteMovimiento = ({ movimiento }) => {
    if (!movimiento) return null;

    const { id, fecha, tipo, notas, monto, Usuario, pagoCompras = [] } = movimiento;
    const empleadoNombre = Usuario?.Empleados?.[0]?.nombreCompleto || "—";
    const pagoDetalle = pagoCompras[0]?.pago || {};

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn} Email: {NegocioConfig.correo}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                        <Text style={{ fontSize: 9, color: '#666' }}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.title}>COMPROBANTE DE {tipo === 'Egresos' ? 'EGRESO' : 'INGRESO'} N° {String(id).padStart(5, '0')}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}><Image style={styles.logo} src={NegocioConfig.logo} /></View>
                    )}
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha y Hora:</Text>
                        <Text style={styles.infoText}>{moment(fecha).format('DD/MM/YYYY HH:mm:ss')}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Responsable:</Text>
                        <Text style={styles.infoText}>{empleadoNombre}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Tipo Movimiento:</Text>
                        <Text style={styles.infoText}>{tipo}</Text>
                    </View>
                </View>

                <View style={styles.contentBox}>
                    <Text style={styles.sectionTitle}>CONCEPTO Y OBSERVACIONES</Text>
                    <Text style={{ lineHeight: 1.5, color: '#444' }}>{notas || "No se especificaron notas adicionales para este movimiento."}</Text>
                </View>

                {(pagoDetalle.pagotarjeta?.length > 0 || pagoDetalle.pagotransferencia?.length > 0) && (
                    <View style={{ marginTop: 15 }}>
                        <Text style={styles.sectionTitle}>DETALLE DE MÉTODOS DE PAGO</Text>
                        {pagoDetalle.pagotarjeta?.map((t, idx) => (
                            <Text key={idx} style={styles.infoText}>• Tarjeta: {formatoMoneda(t.monto)} (Referencia: {t.referencia})</Text>
                        ))}
                        {pagoDetalle.pagotransferencia?.map((t, idx) => (
                            <Text key={idx} style={styles.infoText}>• Transferencia: {formatoMoneda(t.monto)} (Referencia: {t.referencia})</Text>
                        ))}
                    </View>
                )}

                <View style={styles.totalsWrapper}>
                    <View style={{ width: '50%', paddingTop: 10 }}>
                        <Text style={styles.infoLabel}>VALOR EN LETRAS:</Text>
                        <Text style={{ fontSize: 9, fontFamily: 'Tw_Cen_MT_Negrita', textTransform: 'uppercase' }}>
                            {NumeroALetras(monto)}
                        </Text>
                    </View>
                    <View style={styles.totalsContainer}>
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>TOTAL {tipo.toUpperCase()}:</Text>
                            <Text style={styles.grandTotalValue}>{formatoMoneda(monto)}</Text>
                        </View>
                    </View>
                </View>

                <View style={{ marginTop: 60, flexDirection: 'row', justifyContent: 'center' }}>
                    <View style={{ borderTopWidth: 1, borderColor: '#333', width: 200, alignItems: 'center', paddingTop: 5 }}>
                        <Text style={styles.infoLabel}>Firma de Responsable</Text>
                        <Text style={{ fontSize: 8, color: '#666' }}>{empleadoNombre}</Text>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text>Este documento es un comprobante interno de movimientos de caja.</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ReporteMovimiento;